import { BusinessSource } from '@/../../common/src/api/congraduc';
import { LoanType } from '@/interface/CL/clCredit';
import { CouponData } from '@/interface/KKC/coupon';
import {
  BankCardType,
  ContractInfoType,
  LoanEntity,
  repaymentPlanList,
} from '@/interface/KKC/withdrawal';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface withdrawalType {
  bankCardList: BankCardType[];
  loanMaturity: number;
  loanAmt: number;
  applyId: string;
  loanType: LoanType | string;
  divRepaymentTime: string;
  coupon?: CouponData;
  firstRepaymentAmt?: number;
}

type billAccessDataType = {
  id: string;
  businessSource: BusinessSource;
  delFlag: string; // 0代表有效 1代表失效
  tokens: string[];
};

interface CommonState {
  withdrawal: withdrawalType;
  loanInfo: LoanEntity;
  repaymentPlanList: repaymentPlanList;
  loanResultData: {
    paymentBankFlows: BankCardType[];
  };
  errorMsg: string | null;
  clawerErrorMsg: string | null; // 爬虫返回得错误信息
  billAdmitanceErrorMsg: string | null; // 账单准入校验失败错误信息
  contractInfo: ContractInfoType | null;
  withdrawToken: string | null;
  orderId: string | null; // 放款时候的orderId，从银行中带出来的数据
  billAccessData: billAccessDataType | null;
  originOrderId: string | null; // 后来加的一个orderId，后端需要的
  loanType?: LoanType | string;
}

const initialState: CommonState = {
  withdrawal: {
    bankCardList: [],
    loanMaturity: 0,
    loanAmt: 0,
    applyId: '',
    loanType: '01',
    divRepaymentTime: '',
    firstRepaymentAmt: 0,
  },
  loanInfo: {},
  repaymentPlanList: {
    repaymentPeriods: [],
    repaymentPlans: [],
    totalInterest: 0,
    repaymentAmt: 0,
    firstRepaymentAmt: 0,
  },
  loanResultData: {
    paymentBankFlows: [],
  },
  errorMsg: null,
  clawerErrorMsg: null,
  billAdmitanceErrorMsg: null,
  contractInfo: null,
  withdrawToken: null,
  orderId: null,
  billAccessData: null,
  originOrderId: null,
  loanType: '',
};

const withdrawalSlice = createSlice({
  name: 'withdrawStore',
  initialState,
  reducers: {
    setWithdrawal(state: CommonState, action: PayloadAction<{ withdrawal: withdrawalType }>) {
      state.withdrawal = action.payload.withdrawal;
      state.withdrawal.loanType = action.payload.withdrawal.loanType || '01';
    },
    setLoanInfo(state: CommonState, action: PayloadAction<{ loanInfo: LoanEntity }>) {
      state.loanInfo = action.payload.loanInfo;
    },
    setMoreLoanInfo(state: CommonState, action: PayloadAction<{ loanInfo: LoanEntity }>) {
      state.loanInfo = {
        ...state.loanInfo,
        ...action.payload.loanInfo,
      };
    },

    setRepaymentPlans(
      state: CommonState,
      action: PayloadAction<{ repaymentPlanList: repaymentPlanList }>
    ) {
      state.repaymentPlanList = action.payload.repaymentPlanList;
    },
    setLoanResultData(
      state: CommonState,
      action: PayloadAction<{
        loanResultData: {
          paymentBankFlows: BankCardType[];
        };
      }>
    ) {
      state.loanResultData = action.payload.loanResultData;
    },
    setErrorMsg(state: CommonState, action: PayloadAction<{ errorMsg: string }>) {
      state.errorMsg = action.payload.errorMsg;
    },
    setBillAdmitanceErrorMsg(
      state: CommonState,
      action: PayloadAction<{ billAdmitanceErrorMsg: string }>
    ) {
      state.billAdmitanceErrorMsg = action.payload.billAdmitanceErrorMsg;
    },
    setBillAccessData(
      state: CommonState,
      action: PayloadAction<{ billAccessData: billAccessDataType }>
    ) {
      state.billAccessData = action.payload.billAccessData;
    },
    clearBillAdmitanceErrorMsg(state: CommonState) {
      state.billAdmitanceErrorMsg = null;
    },
    clearErrorMsg(state: CommonState) {
      state.errorMsg = null;
    },
    setClawerErrMsg(state: CommonState, action: PayloadAction<{ errorMsg: string }>) {
      state.clawerErrorMsg = action.payload.errorMsg;
    },
    clearClawerErrMsg(state: CommonState) {
      state.clawerErrorMsg = null;
    },
    setContractInfo(state: CommonState, action: PayloadAction<{ info: ContractInfoType }>) {
      state.contractInfo = action.payload.info;
    },
    clearContractInfo(state: CommonState) {
      state.contractInfo = null;
    },
    setWithdrawalToken(state: CommonState, action: PayloadAction<{ token: string }>) {
      state.withdrawToken = action.payload.token;
    },
    clearWithdrawalToken(state: CommonState) {
      state.withdrawToken = null;
    },
    setOrderId(state: CommonState, action: PayloadAction<{ orderId: string }>) {
      state.orderId = action.payload.orderId;
    },
    clearOrderId(state: CommonState) {
      state.orderId = null;
    },
    clearBillAccessData(state: CommonState) {
      state.billAccessData = null;
    },
    setOriginOrderId(state: CommonState, action: PayloadAction<{ originOrderId: string }>) {
      state.originOrderId = action.payload.originOrderId;
    },
    setLoanType(state: CommonState, action: PayloadAction<{ loanType: LoanType | string }>) {
      state.loanType = action.payload.loanType;
    },
  },
});

const {
  setWithdrawal,
  setLoanInfo,
  setMoreLoanInfo,
  setRepaymentPlans,
  setLoanResultData,
  setErrorMsg,
  clearErrorMsg,
  setClawerErrMsg,
  clearClawerErrMsg,
  setContractInfo,
  clearContractInfo,
  setWithdrawalToken,
  clearWithdrawalToken,
  setOrderId,
  clearOrderId,
  setBillAdmitanceErrorMsg,
  clearBillAdmitanceErrorMsg,
  setBillAccessData,
  clearBillAccessData,
  setOriginOrderId,
  setLoanType,
} = withdrawalSlice.actions;

export {
  clearBillAccessData,
  clearBillAdmitanceErrorMsg,
  clearClawerErrMsg,
  clearContractInfo,
  clearErrorMsg,
  clearOrderId,
  clearWithdrawalToken,
  setBillAccessData,
  setBillAdmitanceErrorMsg,
  setClawerErrMsg,
  setContractInfo,
  setErrorMsg,
  setLoanInfo,
  setLoanResultData,
  setMoreLoanInfo,
  setOrderId,
  setOriginOrderId,
  setRepaymentPlans,
  setWithdrawal,
  setWithdrawalToken,
  setLoanType,
};

export default withdrawalSlice.reducer;
